import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { API } from "aws-amplify";

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddAPhotoOutlinedIcon from "@material-ui/icons/AddAPhotoOutlined";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";
import AddPhotoAlbumIcon from "@material-ui/icons/VoiceChat";
import DeleteIcon from "@material-ui/icons/Delete";
import PhotoSizeSelectLargeOutlinedIcon from "@material-ui/icons/PhotoSizeSelectLargeOutlined";
import PhotoSizeSelectSmallOutlinedIcon from "@material-ui/icons/PhotoSizeSelectSmallOutlined";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { grey, deepOrange } from "@material-ui/core/colors";

import { Confirm, useNotify } from "react-admin";
import { getPhotoUrl, getPhoto, getFileKey } from "../../utils/fileUtils";
import {
  MAX_PHOTO_SIZE,
  MAX_PHOTO_SIZE_MSG,
  PhotoStatus,
  PhotoSubStatus,
  PHOTO_TRANSFER_COPY,
  PHOTO_UPDATE_PARENT_DEBOUNCE_MS,
  StatusMessages,
} from "../../utils/constants";
import { logError } from "../../utils/appUtils";
import { CancelIcon } from "./iconography";
import { generateSafeFileKey } from "../../utils/uploadDb";
import { usePhotoTransfers } from "../../context/PhotoTransferContext";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    marginRight: theme.spacing(2),
    color: theme.palette.getContrastText(grey[300]),
    backgroundColor: grey[300],
  },
  iconButtonDisabled: {
    marginRight: theme.spacing(2),
    color: theme.palette.getContrastText(grey[500]),
    backgroundColor: grey[500],
  },
  deleteEnabled: {
    marginRight: theme.spacing(2),
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
  },
  deleteDisabled: {
    marginRight: theme.spacing(2),
    color: theme.palette.getContrastText(grey[500]),
    backgroundColor: grey[500],
  },
  cameraButton: {
    marginRight: theme.spacing(2),
  },
  error: { color: theme.palette.error.main },
  imgAvatar: {
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    margin: theme.spacing(1),
    width: theme.spacing(10),
    height: theme.spacing(10),
  },
  imgAvatarSelected: {
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    margin: theme.spacing(1),
    width: theme.spacing(10),
    height: theme.spacing(10),
    border: "4px solid orange",
  },
  imgAvatarLoading: {
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    backgroundColor: grey[400],
    margin: theme.spacing(1),
    width: theme.spacing(10),
    height: theme.spacing(10),
  },
  mediaFull: {
    margin: "0 auto",
    display: "flex",
    justifyContent: "center",
    borderRadius: "4px",
    objectFit: "contain",
    width: "100%",
  },
}));

export default function PhotoManager({
  parentId,
  parentType,
  parentLabel = null,
  disabled = false,
  viewOnly = false,
  minPhotosNum = 0,
  maxPhotosNum = 10,
  thumbNailSize = 96,
  updateParentFn,
}) {
  const classes = useStyles();
  const notify = useNotify();
  const containerRef = useRef();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const clientWidth = containerRef?.current?.clientWidth || window.innerWidth;
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const { photos, startUpload, requestDelete, loadError } =
    usePhotoTransfers(parentId);

  const photoMap = useMemo(() => {
    const map = new Map();
    photos.forEach((photo) => {
      if (!photo || !photo.key) return;
      map.set(photo.key, photo);
    });
    return map;
  }, [photos]);

  const [selectedKey, setSelectedKey] = useState(null);
  const selectedPhoto = selectedKey ? photoMap.get(selectedKey) : null;

  const updateParentTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const latestPhotosRef = useRef(photos);
  const latestUpdateParentFnRef = useRef(updateParentFn);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    latestPhotosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    latestUpdateParentFnRef.current = updateParentFn;
    if (!updateParentFn && updateParentTimeoutRef.current) {
      clearTimeout(updateParentTimeoutRef.current);
      updateParentTimeoutRef.current = null;
    }
  }, [updateParentFn]);

  useEffect(() => {
    const handler = latestUpdateParentFnRef.current;
    if (!handler) return undefined;

    const timeoutId = setTimeout(() => {
      if (updateParentTimeoutRef.current === timeoutId) {
        updateParentTimeoutRef.current = null;
      }
      if (!isMountedRef.current) return;
      const latestHandler = latestUpdateParentFnRef.current;
      if (latestHandler) {
        latestHandler(latestPhotosRef.current);
      }
    }, PHOTO_UPDATE_PARENT_DEBOUNCE_MS);

    updateParentTimeoutRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (updateParentTimeoutRef.current === timeoutId) {
        updateParentTimeoutRef.current = null;
      }
    };
  }, [photos]);

  useEffect(() => {
    const flushPendingUpdate = () => {
      const handler = latestUpdateParentFnRef.current;
      if (handler) {
        handler(latestPhotosRef.current);
      }
    };

    return () => {
      if (updateParentTimeoutRef.current) {
        clearTimeout(updateParentTimeoutRef.current);
        updateParentTimeoutRef.current = null;
        flushPendingUpdate();
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedKey) {
      const firstKey =
        photos.find((p) => p.status === PhotoStatus.DONE)?.key ||
        photos[0]?.key ||
        null;
      setSelectedKey(firstKey);
      return;
    }

    if (!photoMap.has(selectedKey)) {
      const nextKey =
        photos.find((p) => p.status === PhotoStatus.DONE)?.key ||
        photos[0]?.key ||
        null;
      setSelectedKey(nextKey);
    }
  }, [photos, photoMap, selectedKey]);

  useEffect(() => {
    if (
      !selectedKey ||
      !isPreviewVisible ||
      selectedPhoto?.status !== PhotoStatus.DONE
    )
      return;

    getPhoto(selectedKey, setPreviewUrl, clientWidth);
  }, [selectedKey, isPreviewVisible, clientWidth, selectedPhoto?.status]);

  const handleUpload = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      event.target.value = null;

      if (!file) return;
      if (photoMap.size >= maxPhotosNum) {
        notify(PHOTO_TRANSFER_COPY.MAX_PHOTOS_WARNING(maxPhotosNum), "warning");
        return;
      }
      if (file.size > MAX_PHOTO_SIZE) {
        notify(MAX_PHOTO_SIZE_MSG, "warning");
        return;
      }

      const key = generateSafeFileKey(file, parentId, getFileKey);
      const mutationInput = {
        key,
        bucket: API._options.aws_user_files_s3_bucket,
        region: API._options.aws_user_files_s3_bucket_region,
        type: file.type,
        name: file.name,
        reloadYardId: window.appUser.reloadYardId,
        parentId,
        parentType,
        groups: window.appUser.groups,
        owner: window.appUser.owner,
        createdBy: window.appUser.fullName,
      };

      // Select immediately for instant feedback (photo will appear via upsertPhoto in startUpload)
      setSelectedKey(key);
      setIsPreviewVisible(false);

      startUpload({ key, file, mutationInput, parentId, parentLabel })
        .then(() => {
          if (!isMountedRef.current) return;

          if (!navigator.onLine) {
            notify(PHOTO_TRANSFER_COPY.OFFLINE_SAVED_INFO, "info");
          }
        })
        .catch((err) => {
          if (!isMountedRef.current) {
            logError("PhotoManager:handleUpload", err);
            return;
          }
          logError("PhotoManager:handleUpload", err);
          setError(PHOTO_TRANSFER_COPY.QUEUE_SAVE_ERROR);
          notify(PHOTO_TRANSFER_COPY.QUEUE_SAVE_ERROR_TOAST, "error");
        });
    },
    [
      maxPhotosNum,
      notify,
      parentId,
      parentLabel,
      parentType,
      photoMap.size,
      startUpload,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedPhoto) return;
    // Only allow deletion for DONE or ERROR statuses (matches isDeleteDisabled logic)
    if (
      selectedPhoto.status !== PhotoStatus.DONE &&
      selectedPhoto.status !== PhotoStatus.ERROR
    )
      return;
    try {
      setIsPreviewVisible(false);
      setOpen(false);
      await requestDelete({
        key: selectedPhoto.key,
        photoId: selectedPhoto.id,
        parentId,
        status: selectedPhoto.status,
      });
      notify(PHOTO_TRANSFER_COPY.DELETE_SUCCESS, "success");
    } catch (err) {
      logError("PhotoManager:handleDelete", err);
      notify(PHOTO_TRANSFER_COPY.DELETE_FAILURE, "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, requestDelete, selectedPhoto]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
    setIsPreviewVisible(false);
  };

  const togglePreview = () => {
    if (isPreviewDisabled) return;
    setIsPreviewVisible((prev) => !prev);
  };

  const getAvatarClass = useCallback(
    (photo) => {
      if (
        photo.status === PhotoStatus.UPLOADING ||
        photo.status === PhotoStatus.ERROR
      )
        return classes.imgAvatarLoading;
      if (selectedKey === photo?.key) return classes.imgAvatarSelected;
      return classes.imgAvatar;
    },
    [
      classes.imgAvatar,
      classes.imgAvatarLoading,
      classes.imgAvatarSelected,
      selectedKey,
    ],
  );

  const getStatusText = useCallback((photo) => {
    if (photo.statusMessage) return photo.statusMessage;
    if (photo.isPaused) return PHOTO_TRANSFER_COPY.STATUS_PAUSED;
    if (photo.status === PhotoStatus.DONE) {
      return null;
    }
    if (photo.status === PhotoStatus.UPLOADING) {
      if (photo.subStatus === PhotoSubStatus.UPLOADED) {
        return StatusMessages.SAVING;
      }
      if (photo.subStatus === PhotoSubStatus.RETRYING) {
        return PHOTO_TRANSFER_COPY.STATUS_RETRYING;
      }
      if (photo.subStatus === PhotoSubStatus.PAUSED) {
        return StatusMessages.PAUSED_OFFLINE;
      }
      if (photo.progress) {
        return StatusMessages.UPLOADING(Math.round(photo.progress * 100));
      }
      return PHOTO_TRANSFER_COPY.STATUS_UPLOADING;
    }
    if (photo.status === PhotoStatus.ERROR) {
      return StatusMessages.ERROR_GENERIC(
        photo.error || PHOTO_TRANSFER_COPY.STATUS_ERROR_UNKNOWN,
      );
    }
    return PHOTO_TRANSFER_COPY.STATUS_PROCESSING;
  }, []);

  const isPreviewDisabled =
    !isExpanded ||
    photoMap.size === 0 ||
    selectedPhoto?.status !== PhotoStatus.DONE;

  const isDeleteDisabled =
    disabled ||
    !isExpanded ||
    !selectedPhoto ||
    (selectedPhoto.status !== PhotoStatus.DONE &&
      selectedPhoto.status !== PhotoStatus.ERROR);

  const isDisabled = disabled || photoMap.size >= maxPhotosNum;

  if (viewOnly && photoMap.size === 0) return null;

  return (
    <div ref={containerRef}>
      {isExpanded && (
        <Grid container>
          {previewUrl && (
            <Grid item xs={12}>
              <PhotoPreview
                src={previewUrl}
                classes={classes}
                isPreviewVisible={isPreviewVisible}
                clientWidth={clientWidth}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Box display="flex" flexWrap="wrap" justifyContent="center">
              {Array.from(photoMap.entries()).map(([key, photo]) => {
                const statusText = getStatusText(photo);
                const avatar = (
                  <Avatar
                    key={key}
                    className={getAvatarClass(photo)}
                    variant="rounded"
                    src={
                      photo.status === PhotoStatus.DONE
                        ? getPhotoUrl(key, thumbNailSize)
                        : undefined
                    }
                    onClick={() => setSelectedKey(key)}
                  >
                    {photo.status === PhotoStatus.UPLOADING && (
                      <CircularProgress
                        color="inherit"
                        variant={
                          photo.progress ? "determinate" : "indeterminate"
                        }
                        value={(photo.progress || 0) * 100}
                      />
                    )}
                    {photo.status === PhotoStatus.ERROR && (
                      <Typography
                        variant="caption"
                        style={{ fontSize: "0.6rem" }}
                      >
                        !
                      </Typography>
                    )}
                  </Avatar>
                );
                return statusText ? (
                  <Tooltip key={key} title={statusText} arrow>
                    {avatar}
                  </Tooltip>
                ) : (
                  avatar
                );
              })}
            </Box>
          </Grid>
        </Grid>
      )}

      <div
        style={{
          display: "flex",
          marginTop: 16,
          justifyContent: smDown ? "center" : "flex-start",
        }}
      >
        {!viewOnly && (
          <>
            <input
              type="file"
              accept="image/*"
              capture="environment" // force rear camera || "camera"
              id="camera-input"
              style={{ display: "none" }}
              onChange={handleUpload}
              disabled={isDisabled}
            />
            {xsDown ? (
              <label htmlFor="camera-input">
                <Avatar
                  className={
                    isDisabled ? classes.iconButtonDisabled : classes.iconButton
                  }
                  style={{ cursor: isDisabled ? "default" : "pointer" }}
                  component="span"
                >
                  {minPhotosNum > 0 ? (
                    <AddAPhotoOutlinedIcon />
                  ) : (
                    <PhotoCameraOutlinedIcon />
                  )}
                </Avatar>
              </label>
            ) : (
              <label htmlFor="camera-input">
                <Button
                  variant="outlined"
                  size="large"
                  className={classes.cameraButton}
                  component="span"
                  startIcon={
                    minPhotosNum > 0 ? (
                      <AddAPhotoOutlinedIcon />
                    ) : (
                      <PhotoCameraOutlinedIcon />
                    )
                  }
                  disabled={isDisabled}
                >
                  CAMERA
                </Button>
              </label>
            )}
            <input
              type="file"
              accept="image/*"
              id="gallery-input"
              style={{ display: "none" }}
              onChange={handleUpload}
              disabled={isDisabled}
            />
            <label htmlFor="gallery-input">
              <Avatar
                className={
                  isDisabled ? classes.iconButtonDisabled : classes.iconButton
                }
                style={{ cursor: isDisabled ? "default" : "pointer" }}
                component="span"
              >
                <AddPhotoAlbumIcon />
              </Avatar>
            </label>
            <Avatar
              className={
                isDeleteDisabled
                  ? classes.deleteDisabled
                  : classes.deleteEnabled
              }
              style={{ cursor: isDeleteDisabled ? "default" : "pointer" }}
              onClick={() => !isDeleteDisabled && setOpen(true)}
            >
              <DeleteIcon />
            </Avatar>
          </>
        )}
        <Avatar
          style={{ cursor: "pointer" }}
          className={
            isPreviewDisabled ? classes.iconButtonDisabled : classes.iconButton
          }
          onClick={togglePreview}
        >
          {isPreviewVisible ? (
            <PhotoSizeSelectSmallOutlinedIcon />
          ) : (
            <PhotoSizeSelectLargeOutlinedIcon />
          )}
        </Avatar>
        <Avatar
          style={{ cursor: "pointer" }}
          className={classes.iconButton}
          onClick={toggleExpand}
        >
          {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </Avatar>
      </div>

      {(loadError || error) && (
        <Typography className={classes.error}>{loadError || error}</Typography>
      )}

      <Confirm
        isOpen={open}
        title={PHOTO_TRANSFER_COPY.DELETE_CONFIRM_TITLE}
        content={PHOTO_TRANSFER_COPY.DELETE_CONFIRM_MESSAGE}
        onConfirm={handleDelete}
        onClose={() => setOpen(false)}
        CancelIcon={CancelIcon}
      />
    </div>
  );
}

const PhotoPreview = ({ src, classes, isPreviewVisible, clientWidth }) => {
  const previewRef = useRef();

  const scrollToMe = () => {
    const el = previewRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = window.pageYOffset + rect.top - 20;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <img
      ref={previewRef}
      src={src}
      alt="Preview"
      onLoad={scrollToMe}
      className={classes.mediaFull}
      style={{
        display: isPreviewVisible ? "block" : "none",
        height: clientWidth / 2,
      }}
    />
  );
};
